import React, { useEffect, useRef, useState } from "react";
import { NavigationBackButton } from "../NavigationBackButton";
import { StrengthWheel } from "./StrengthWheel";
import {
    useContextMeshScoreRefresh,
    useMeshStrengthData,
    useWorkspaceContextMeshScoreRefresh,
    useWorkspaceMeshStrengthData
} from "./useMeshStrengthData";
import { ContextMeshScoreReport } from "./ContextMeshScoreReport";
import type {
    ContextMeshAnalysis,
    ContextMeshCategoryAnalysisStatus,
    ContextMeshCategoryBand,
    ContextMeshCategoryKey,
    ContextMeshCategoryScore,
    ContextMeshRecommendation,
    ContextMeshSourceDiagnostic
} from "./contextMeshAnalysis";
import {
    sourceDiagnosticBadge,
    sourceDiagnosticState
} from "./contextMeshSourceDiagnostics";
import {
    CONTEXT_MESH_CATEGORIES,
    type ContextMeshCategoryTheme
} from "./contextMeshTheme";
import "../newProduct/tokens.css";

export interface MeshStrengthViewProps {
    projectId: number | null;
    contextMeshId?: string | null;
    contextMeshTitle?: string | null;
    canRefreshScore?: boolean;
    refreshSignal?: number;
    onNavigate: (view: "integrations" | "uploads" | "projects") => void;
    onOpenRefresh?: (projectId: number) => void;
    onOpenWorkspaceFiles?: () => void;
    onOpenWorkspaceIntegrations?: () => void;
    onOpenWorkspaceRefresh?: () => void;
    onBackToFeatureStudio: () => void;
    backLabel?: string;
}

type CategoryMeta = ContextMeshCategoryTheme & {
    actionLabel: string;
    destination: "refresh" | "integrations" | "uploads";
};

type DisplayBand = {
    label: "Strong" | "Good" | "Thin" | "Weak";
    tone: string;
    plain: string;
};

const CATEGORY_ACTIONS: Record<ContextMeshCategoryKey, Pick<CategoryMeta, "actionLabel" | "destination">> = {
    discover: { actionLabel: "Fill in product details", destination: "refresh" },
    external: { actionLabel: "Open Integrations", destination: "integrations" },
    file: { actionLabel: "Open Files", destination: "uploads" },
    code: { actionLabel: "Open Integrations", destination: "integrations" }
};

const CATEGORIES: CategoryMeta[] = CONTEXT_MESH_CATEGORIES.map(category => ({
    ...category,
    ...CATEGORY_ACTIONS[category.key]
}));

const BAND_COLORS: Record<ContextMeshCategoryBand, string> = {
    STRONG: "#059669",
    GOOD: "#0d9488",
    THIN: "#d97706",
    WEAK: "#dc2626"
};

const cardStyle: React.CSSProperties = {
    padding: "13px 15px",
    borderRadius: 13,
    background: "var(--bg-paper)",
    border: "1px solid var(--divider)",
    boxShadow: "var(--shadow-1)"
};

const eyebrowStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "var(--fg4)"
};

function friendlyField(field: string): string {
    return field.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ").replace(/^./, value => value.toUpperCase());
}

const BASELINE_FIELD_LABELS: Record<string, string> = {
    productName: "Product Name",
    product_name: "Product Name",
    description: "What Solution Does The Product Offer",
    productDescription: "What Solution Does The Product Offer",
    product_description: "What Solution Does The Product Offer",
    problemStatement: "Problem Product Solves",
    problem_statement: "Problem Product Solves"
};

function baselineFieldLabel(field: string): string {
    return BASELINE_FIELD_LABELS[field] ?? friendlyField(field);
}

function baselineRecommendation(field: string): string | null {
    const label = baselineFieldLabel(field);
    if (label === "Product Name") return "Add a clear Product Name.";
    if (label === "Problem Product Solves") return "Describe the user pain and its impact under Problem Product Solves.";
    if (label === "What Solution Does The Product Offer") return "Describe the proposed solution under What Solution Does The Product Offer.";
    return null;
}

function freshness(analysis: ContextMeshAnalysis): { title: string; detail: string; color: string; background: string; retryable?: boolean } | null {
    const semantic = analysis.semanticAnalysis;
    if (semantic.resultSource === "last_ready") {
        if (semantic.status === "pending") return {
            title: "Analysis is updating",
            detail: "Showing the last completed score while the current product snapshot is analyzed.",
            color: "#92400e",
            background: "#fffbeb"
        };
        if (semantic.status === "failed") return {
            title: "Latest analysis failed",
            detail: "Showing the last completed score. Retry after the underlying issue is resolved.",
            color: "#b91c1c",
            background: "#fef2f2",
            retryable: true
        };
        return {
            title: "Analysis is outdated",
            detail: "Showing the last completed score for an earlier product snapshot.",
            color: "#92400e",
            background: "#fffbeb",
            retryable: true
        };
    }
    const category = analysis.categoryAnalysis;
    if (category?.status === "failed") return {
        title: "Latest category analysis failed",
        detail: category.isStale && analysis.categoryScores
            ? "Showing the last completed category scores."
            : "Canonical category scores are not available yet.",
        color: "#b91c1c",
        background: "#fef2f2"
    };
    if (category?.status === "pending") return {
        title: "Category analysis is in progress",
        detail: category.isStale && analysis.categoryScores
            ? "Showing the last completed category scores while the current snapshot is analyzed."
            : "Canonical category scores will appear when analysis completes.",
        color: "#1d4ed8",
        background: "#eff6ff"
    };
    return null;
}

function displayBand(score: number): DisplayBand {
    if (score >= 80) return { label: "Strong", tone: "#059669", plain: "The AI has what it needs here." };
    if (score >= 55) return { label: "Good", tone: "#0d9488", plain: "Solid. A couple of gaps left." };
    if (score >= 30) return { label: "Thin", tone: "#d97706", plain: "The AI is filling in blanks here." };
    return { label: "Weak", tone: "#dc2626", plain: "Mostly guesswork. Biggest win available." };
}

const RECOMMENDATION_CODE_HINTS: Record<ContextMeshCategoryKey, string[]> = {
    discover: ["PROBLEM", "SOLUTION", "PRODUCT", "PERSONA", "USER", "REQUIREMENT", "ACCEPTANCE", "STORY", "FEATURE"],
    external: ["JIRA", "AZURE", "DEVOPS", "INTEGRATION", "SYNC", "TRACKER", "RELEASE"],
    file: ["FILE", "DOCUMENT", "FIGMA", "UPLOAD", "SPEC", "DESIGN"],
    code: ["CODE", "REPOSITORY", "REPO", "GIT", "DATABASE", "SCHEMA", "TEST", "BUILD", "API", "ARCHITECTURE"]
};

function recommendationCategory(recommendation: ContextMeshRecommendation): ContextMeshCategoryKey | null {
    if (recommendation.contextCategory) return recommendation.contextCategory;

    const entityTypes = new Set(recommendation.entityRefs.map(ref => ref.type));
    if (entityTypes.has("repository")) return "code";
    if (entityTypes.has("integration")) return "external";
    if (entityTypes.has("document") || entityTypes.has("documents")) return "file";
    if (["product", "persona", "feature", "user_story", "stories"].some(type => entityTypes.has(type))) return "discover";

    const code = recommendation.code.toUpperCase();
    return CATEGORIES.find(category => RECOMMENDATION_CODE_HINTS[category.key].some(hint => code.includes(hint)))?.key ?? null;
}

function categoryWins(analysis: ContextMeshAnalysis, weakest: CategoryMeta | null): Partial<Record<ContextMeshCategoryKey, ContextMeshRecommendation>> {
    const wins: Partial<Record<ContextMeshCategoryKey, ContextMeshRecommendation>> = {};
    const recommendations = [analysis.nextBestAction, ...analysis.recommendationDetails]
        .filter((item): item is ContextMeshRecommendation => item !== null)
        .filter((item, index, all) => all.findIndex(candidate => candidate.recommendationId === item.recommendationId) === index)
        .sort((left, right) => left.priority - right.priority || (right.estimatedScoreDelta ?? 0) - (left.estimatedScoreDelta ?? 0));

    for (const recommendation of recommendations) {
        const category = recommendationCategory(recommendation);
        if (category && !wins[category]) wins[category] = recommendation;
    }

    if (analysis.nextBestAction && weakest) {
        const mappedCategory = recommendationCategory(analysis.nextBestAction) ?? weakest.key;
        wins[mappedCategory] = analysis.nextBestAction;
    }
    return wins;
}

function CategoryLane({
    category,
    score,
    recommendation,
    recommendationStatus,
    onRetryRecommendation,
    categoryStatus,
    sourceDiagnostic,
    active,
    onAction,
    onHover
}: {
    category: CategoryMeta;
    score: ContextMeshCategoryScore | null;
    recommendation: ContextMeshRecommendation | null;
    recommendationStatus?: "not_requested" | "analyzing" | "ready" | "failed";
    onRetryRecommendation?: () => void;
    categoryStatus: ContextMeshCategoryAnalysisStatus | null;
    sourceDiagnostic: ContextMeshSourceDiagnostic | null;
    active: boolean;
    onAction: (() => void) | null;
    onHover: (key: ContextMeshCategoryKey | null) => void;
}) {
    const bandColor = score ? BAND_COLORS[score.band] : "var(--fg4)";
    const currentState = sourceDiagnostic && category.key !== "discover"
        ? sourceDiagnosticState(
            category.key,
            sourceDiagnostic,
            score?.band === "WEAK" || score?.band === "THIN",
            score?.summary
        )
        : score?.summary
            ?? (categoryStatus === "pending"
                ? "Canonical category scoring is in progress."
                : categoryStatus === "failed"
                  ? "Canonical category analysis did not complete."
                  : "Detailed category scoring is not available for this analysis.");
    const recommendationMessage = recommendationStatus === "not_requested" || recommendationStatus === "analyzing"
        ? "Analyzing recommendation…"
        : recommendationStatus === "failed"
          ? "Recommendation unavailable — Retry."
          : recommendation?.message ?? "No prioritized improvement returned";
    return (
        <div
            data-testid={`context-source-${category.key}`}
            onMouseEnter={() => onHover(category.key)}
            onMouseLeave={() => onHover(null)}
            style={{
                ...cardStyle,
                display: "flex",
                gap: 13,
                borderRadius: 14,
                borderColor: active ? category.accentColor : "var(--divider)",
                background: active ? `color-mix(in srgb, ${category.color} 6%, var(--bg-paper))` : "var(--bg-paper)",
                boxShadow: active ? "var(--shadow-2)" : "var(--shadow-1)",
                transition: "border-color 160ms ease, background 160ms ease, box-shadow 160ms ease"
            }}
        >
            <span
                aria-hidden="true"
                style={{
                    width: 6,
                    flex: "0 0 auto",
                    borderRadius: 4,
                    background: category.color,
                    opacity: score ? 0.28 + 0.72 * (score.score / 100) : 0.28
                }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 9, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: "var(--fg1)" }}>{category.title}</span>
                    <span style={{ fontSize: 11.5, color: "var(--fg4)" }}>{category.subtitle}</span>
                    {sourceDiagnostic && category.key !== "discover" && (
                        <span
                            data-testid={`source-status-${category.key}`}
                            style={{
                                padding: "2px 7px",
                                borderRadius: 999,
                                background: "#eff6ff",
                                color: "#1d4ed8",
                                fontSize: 10,
                                fontWeight: 700,
                                whiteSpace: "nowrap"
                            }}
                        >
                            {sourceDiagnosticBadge(category.key, sourceDiagnostic)}
                        </span>
                    )}
                    <span style={{ flex: 1 }} />
                    <span style={{ fontSize: 20, lineHeight: 1, fontFamily: "var(--font-mono)", fontWeight: 700, color: score ? category.accentColor : "var(--fg4)" }}>
                        {score?.score ?? "—"}
                    </span>
                    <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: bandColor }}>
                        {score ? score.band : categoryStatus === "pending" ? "Analyzing" : "Not rated"}
                    </span>
                </div>
                <div style={{ height: 6, borderRadius: 6, background: "#e2e8f0", overflow: "hidden", margin: "9px 0 8px" }}>
                    {score && <span style={{ display: "block", width: `${score.score}%`, height: "100%", borderRadius: 6, background: category.color }} />}
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
                    <span style={{ ...eyebrowStyle, flex: "0 0 auto" }}>Current state</span>
                    <span style={{ minWidth: 0, flex: 1, fontSize: 12, lineHeight: 1.45, color: "var(--fg3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: sourceDiagnostic ? "normal" : "nowrap" }}>
                        {currentState}
                    </span>
                    {onAction && (
                        <button
                            type="button"
                            data-testid={`lane-cta-${category.key}`}
                            onClick={onAction}
                            style={{ flex: "0 0 auto", padding: 0, border: "none", background: "none", color: category.accentColor, fontFamily: "inherit", fontSize: 11.5, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}
                        >
                            {category.actionLabel} →
                        </button>
                    )}
                </div>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 9, paddingTop: 9, borderTop: "1px solid var(--divider)", minWidth: 0 }}>
                    <span style={{ ...eyebrowStyle, flex: "0 0 auto" }}>Score method</span>
                    <span
                        data-testid={`lane-score-method-${category.key}`}
                        style={{ minWidth: 0, flex: 1, fontSize: 12, lineHeight: 1.45, color: score?.method ? "var(--fg3)" : "var(--fg4)", whiteSpace: "normal", overflowWrap: "anywhere" }}
                    >
                        {score?.method ?? "Score method is not available for this analysis."}
                    </span>
                </div>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 9, paddingTop: 9, borderTop: "1px solid var(--divider)", minWidth: 0 }}>
                    <span style={{ ...eyebrowStyle, flex: "0 0 auto" }}>Recommendation</span>
                    {recommendationStatus === "failed" && onRetryRecommendation ? (
                        <button type="button" onClick={onRetryRecommendation} style={{ minWidth: 0, flex: 1, padding: 0, border: 0, background: "none", color: "var(--fg4)", fontFamily: "inherit", fontSize: 12.5, lineHeight: 1.5, textAlign: "left", cursor: "pointer" }}>
                            {recommendationMessage}
                        </button>
                    ) : (
                        <span
                            data-testid={`lane-recommendation-${category.key}`}
                            style={{ minWidth: 0, flex: 1, fontSize: 12.5, lineHeight: 1.45, color: recommendation || recommendationStatus === "analyzing" ? "var(--fg2)" : "var(--fg4)", whiteSpace: "normal", overflowWrap: "anywhere" }}
                        >
                            {recommendationMessage}
                        </span>
                    )}
                    {recommendation && (recommendationStatus == null || recommendationStatus === "ready") && (recommendation.estimatedScoreDelta ?? 0) > 0 && (
                        <span style={{ flex: "0 0 auto", fontSize: 11.5, fontWeight: 700, color: category.accentColor }}>+{recommendation.estimatedScoreDelta} pts</span>
                    )}
                </div>
            </div>
        </div>
    );
}

function HeaderStat({ color, children }: { color: string; children: React.ReactNode }) {
    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7, whiteSpace: "nowrap" }}>
            <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 4, background: color }} />
            <span style={{ fontSize: 12, color: "var(--fg3)" }}>{children}</span>
        </span>
    );
}

function HeaderStatButton({ color, children, onClick }: { color: string; children: React.ReactNode; onClick: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            title="Go to Do this next"
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                padding: "3px 5px",
                border: "none",
                borderRadius: 6,
                background: "transparent",
                color: "var(--fg3)",
                fontFamily: "inherit",
                fontSize: 12,
                cursor: "pointer",
                whiteSpace: "nowrap"
            }}
        >
            <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: 4, background: color }} />
            <span>{children}</span>
        </button>
    );
}

export function MeshStrengthView({
    projectId,
    contextMeshId = null,
    contextMeshTitle = null,
    canRefreshScore = false,
    onNavigate,
    onOpenRefresh,
    onOpenWorkspaceFiles,
    onOpenWorkspaceIntegrations,
    onOpenWorkspaceRefresh,
    onBackToFeatureStudio,
    backLabel = "Back to Feature Studio",
    refreshSignal = 0
}: MeshStrengthViewProps) {
    const standalone = contextMeshId != null;
    const productData = useMeshStrengthData(standalone ? null : projectId, refreshSignal);
    const workspaceData = useWorkspaceMeshStrengthData(standalone ? contextMeshId : null, refreshSignal);
    const { analysis, status, loading, error, retry, retryRecommendations } = standalone ? workspaceData : productData;
    const productRefresh = useContextMeshScoreRefresh(standalone ? null : projectId, productData.retry);
    const workspaceRefresh = useWorkspaceContextMeshScoreRefresh(standalone ? contextMeshId : null, workspaceData.retry);
    const { refreshing, refreshNotice, refreshScore } = standalone ? workspaceRefresh : productRefresh;
    const scoreRecovery = standalone && canRefreshScore ? (
        <div style={{ display: "grid", gap: 8, justifyItems: "center", marginTop: 12 }}>
            <span style={{ fontSize: 12.5, color: "var(--fg3)" }}>Refresh the score to analyze this mesh’s current context.</span>
            <button
                type="button"
                data-ui-bridge="mesh-strength.refresh-score"
                data-ui-label="Refresh score"
                disabled={refreshing}
                onClick={() => void refreshScore()}
                style={{ minHeight: 34, padding: "0 14px", borderRadius: 9, border: "1px solid var(--np-brand, #2733C2)", background: "var(--bg-paper)", color: "var(--np-brand, #2733C2)", fontFamily: "inherit", fontSize: 12, fontWeight: 700, cursor: refreshing ? "wait" : "pointer", opacity: refreshing ? 0.65 : 1 }}
            >
                {refreshing ? "Refreshing…" : "Refresh score"}
            </button>
            {refreshNotice && <span role={refreshNotice.kind === "error" ? "alert" : "status"} style={{ fontSize: 12.5, color: refreshNotice.kind === "error" ? "#b91c1c" : "var(--fg3)" }}>{refreshNotice.message}</span>}
        </div>
    ) : null;
    const [activeCategory, setActiveCategory] = useState<ContextMeshCategoryKey | null>(null);
    const [reportOpen, setReportOpen] = useState(false);
    const nextActionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!standalone && projectId == null) onNavigate("projects");
    }, [projectId, onNavigate, standalone]);
    useEffect(() => setReportOpen(false), [contextMeshId, projectId]);
    if ((!standalone && projectId == null) || (standalone && contextMeshId == null)) return null;

    const baselineIncomplete = error?.code === "BASELINE_INCOMPLETE";
    const mappedBaselineRecommendations = error?.missingFields
        .map(field => baselineRecommendation(field))
        .filter((item): item is string => Boolean(item)) ?? [];
    const baselineRecommendations = mappedBaselineRecommendations.length > 0
        ? mappedBaselineRecommendations
        : error?.recommendations ?? [];
    const stateBanner = analysis ? freshness(analysis) : null;
    const coverage = analysis?.semanticAnalysis.coverage;
    const weakestCategory = analysis?.categoryScores
        ? CATEGORIES.reduce((weakest, category) =>
            analysis.categoryScores![category.key].score < analysis.categoryScores![weakest.key].score ? category : weakest
        )
        : null;
    const biggestWins = analysis ? categoryWins(analysis, weakestCategory) : {};
    const overallBand = analysis ? displayBand(analysis.score) : null;
    const generatedCategoryRecommendations = analysis?.categoryRecommendations
        ? Object.values(analysis.categoryRecommendations)
            .map(state => state.recommendation)
            .filter((item): item is ContextMeshRecommendation => item !== null)
            .sort((left, right) => left.priority - right.priority)
        : null;
    const recommendationsPending = analysis?.categoryRecommendations != null && Object.values(analysis.categoryRecommendations).some(
        state => state.status === "not_requested" || state.status === "analyzing"
    );
    const recommendationsFailed = analysis?.categoryRecommendations != null
        && Object.values(analysis.categoryRecommendations).every(state => state.status === "failed");
    const nextBestAction = generatedCategoryRecommendations
        ? generatedCategoryRecommendations[0] ?? null
        : analysis?.nextBestAction ?? null;
    const nextActionCategoryKey = nextBestAction
        ? recommendationCategory(nextBestAction) ?? weakestCategory?.key ?? null
        : null;
    const nextActionCategory = nextBestAction
        ? CATEGORIES.find(category => category.key === nextActionCategoryKey) ?? null
        : null;
    const priorityActionCount = analysis?.tierBasis.blockingGapCodes.length ?? 0;
    const priorityActionLabel = `${priorityActionCount} priority ${priorityActionCount === 1 ? "action" : "actions"}`;
    const goToNextAction = () => {
        const target = nextActionRef.current;
        if (!target) return;
        target.scrollIntoView({ behavior: "smooth", block: "center" });
        target.focus({ preventScroll: true });
    };

    if (reportOpen && analysis) {
        return (
            <ContextMeshScoreReport
                {...(standalone ? { contextMeshId } : { productId: projectId! })}
                contextMeshTitle={standalone ? contextMeshTitle : null}
                analysis={analysis}
                refreshing={refreshing}
                refreshNotice={refreshNotice}
                onRefresh={refreshScore}
                onClose={() => setReportOpen(false)}
            />
        );
    }

    return (
        <div className="np-v6" data-ui-bridge="mesh-strength" style={{ height: "100%", display: "flex", flexDirection: "column", background: "var(--bg)", overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "16px 28px", borderBottom: "1px solid var(--divider)", background: "var(--bg-paper)", flex: "0 0 auto", flexWrap: "wrap" }}>
                <NavigationBackButton label={backLabel} onClick={onBackToFeatureStudio} description={backLabel} bridgeId="btn.back-to-feature-studio" />
                <div style={{ flex: "1 1 360px", minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <span style={{ fontSize: 22, fontWeight: 650, letterSpacing: "-0.012em", color: "var(--fg1)", whiteSpace: "nowrap" }}>Context Mesh</span>
                        {standalone && contextMeshTitle && (
                            <>
                                <span aria-hidden="true" style={{ fontSize: 20, fontWeight: 300, color: "var(--fg4)" }}>|</span>
                                <span title={contextMeshTitle} style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", fontSize: 14, fontWeight: 500, color: "var(--fg1)", whiteSpace: "nowrap" }}>
                                    {contextMeshTitle}
                                </span>
                            </>
                        )}
                        {coverage && (
                            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--fg4)", padding: "2px 8px", borderRadius: 999, background: "var(--bg-input)" }}>
                                {coverage.applicableDrivers} checks
                            </span>
                        )}
                    </div>
                    <div style={{ fontSize: 13, color: "var(--fg3)", marginTop: 3 }}>
                        How strong each kind of context is for this {standalone ? "Context Mesh" : "product"}.
                    </div>
                </div>
                {coverage && (
                    <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                        <HeaderStat color="#10b981">{coverage.evaluatedDrivers} evaluated</HeaderStat>
                        <HeaderStatButton color="#f59e0b" onClick={goToNextAction}>{priorityActionLabel}</HeaderStatButton>
                        {refreshNotice && (
                            <span
                                role={refreshNotice.kind === "error" ? "alert" : "status"}
                                style={{ maxWidth: 220, fontSize: 11.5, color: refreshNotice.kind === "error" ? "#b91c1c" : "#047857" }}
                            >
                                {refreshNotice.message}
                            </span>
                        )}
                        <button
                            type="button"
                            data-ui-bridge="mesh-strength.refresh-score"
                            data-ui-label="Refresh score"
                            data-ui-description="Refresh the current Context Mesh score"
                            disabled={refreshing}
                            onClick={() => void refreshScore()}
                            style={{ minHeight: 34, padding: "0 14px", borderRadius: 9, border: "1px solid var(--np-brand, #2733C2)", background: "var(--bg-paper)", color: "var(--np-brand, #2733C2)", fontFamily: "inherit", fontSize: 12, fontWeight: 700, cursor: refreshing ? "wait" : "pointer", opacity: refreshing ? 0.65 : 1, whiteSpace: "nowrap" }}
                        >
                            {refreshing ? "Refreshing…" : "Refresh score"}
                        </button>
                        <button
                            type="button"
                            data-ui-bridge="mesh-strength.score-detail-report"
                            data-ui-label="Score Detail Report"
                            data-ui-description="Open the Context Mesh Score Detail Report"
                            onClick={() => setReportOpen(true)}
                            style={{ minHeight: 34, padding: "0 14px", borderRadius: 9, border: "1px solid var(--np-brand, #2733C2)", background: "var(--np-brand, #2733C2)", color: "#fff", fontFamily: "inherit", fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}
                        >
                            Score Detail Report
                        </button>
                    </div>
                )}
            </div>

            <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "20px 24px 28px" }}>
                {loading && !analysis && (
                    <div data-testid="mesh-strength-loading" style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
                        <div style={{ flex: "0 1 520px", minWidth: 320, aspectRatio: "1 / 1", borderRadius: "50%", background: "var(--bg-input)" }} />
                        <div style={{ flex: "1 1 440px", minWidth: 320, display: "grid", gap: 10 }}>
                            {[0, 1, 2, 3].map(index => <div key={index} style={{ minHeight: 112, borderRadius: 14, background: "var(--bg-input)" }} />)}
                        </div>
                    </div>
                )}

                {!loading && error && baselineIncomplete && (
                    <div data-testid="mesh-baseline-incomplete" style={{ ...cardStyle, maxWidth: 680, margin: "48px auto", padding: "16px 18px" }}>
                        <div style={{ fontSize: 18, fontWeight: 700, color: "var(--fg1)" }}>Complete the product baseline</div>
                        <div style={{ fontSize: 13, color: "var(--fg3)", lineHeight: 1.55, marginTop: 6 }}>Complete the required product details below so Context Mesh can analyze this product.</div>
                        {error.missingFields.length > 0 && (
                            <div style={{ marginTop: 15 }}>
                                <div style={eyebrowStyle}>Missing information</div>
                                <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 8 }}>
                                    {error.missingFields.map(field => <span key={field} style={{ padding: "5px 9px", borderRadius: 999, background: "#fff7ed", color: "#9a3412", fontSize: 12 }}>{baselineFieldLabel(field)}</span>)}
                                </div>
                            </div>
                        )}
                        {baselineRecommendations.length > 0 && <ul style={{ margin: "15px 0 0", paddingLeft: 20, color: "var(--fg2)", fontSize: 13, lineHeight: 1.7 }}>{baselineRecommendations.map(item => <li key={item}>{item}</li>)}</ul>}
                        {!standalone && onOpenRefresh && <button type="button" onClick={() => onOpenRefresh(projectId!)} style={{ marginTop: 16, minHeight: 36, padding: "0 15px", border: "none", borderRadius: 9, background: "var(--np-brand, #103699)", color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>Complete product context</button>}
                    </div>
                )}

                {!loading && error && !baselineIncomplete && (
                    <div style={{ ...cardStyle, maxWidth: 520, margin: "60px auto", padding: "16px 18px", textAlign: "center" }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "var(--fg1)" }}>{error.status === 404 ? "No Context Mesh analysis was found" : error.status === 401 || error.status === 403 ? "You don’t have access to this analysis" : "Context analysis is unavailable"}</div>
                        <div style={{ fontSize: 12.5, color: "var(--fg3)", lineHeight: 1.5, margin: "7px 0 13px" }}>{error.message}</div>
                        <button type="button" onClick={retry} style={{ height: 34, padding: "0 16px", borderRadius: 9, border: "1px solid var(--divider)", background: "var(--bg-paper)", fontSize: 13, fontWeight: 650, cursor: "pointer", fontFamily: "inherit", color: "var(--fg1)" }}>Retry</button>
                        {error.status >= 500 && scoreRecovery}
                    </div>
                )}

                {!loading && !error && !analysis && status === "analyzing" && (
                    <div data-testid="mesh-strength-analyzing" style={{ ...cardStyle, maxWidth: 520, margin: "60px auto", padding: "20px 22px", textAlign: "center" }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "var(--fg1)" }}>Context Mesh is being analyzed</div>
                        <div style={{ fontSize: 12.5, color: "var(--fg3)", lineHeight: 1.5, marginTop: 7 }}>The final score will appear when canonical analysis completes.</div>
                    </div>
                )}

                {!loading && !error && !analysis && status === "unavailable" && (
                    <div data-testid="mesh-strength-unavailable" style={{ ...cardStyle, maxWidth: 520, margin: "60px auto", padding: "20px 22px", textAlign: "center" }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "var(--fg1)" }}>Context analysis is unavailable</div>
                        <div style={{ fontSize: 12.5, color: "var(--fg3)", lineHeight: 1.5, margin: "7px 0 13px" }}>No canonical Context Mesh score is available.</div>
                        <button type="button" onClick={retry} style={{ height: 34, padding: "0 16px", borderRadius: 9, border: "1px solid var(--divider)", background: "var(--bg-paper)", fontSize: 13, fontWeight: 650, cursor: "pointer", fontFamily: "inherit", color: "var(--fg1)" }}>Retry</button>
                        {scoreRecovery}
                    </div>
                )}

                {analysis && overallBand && (
                    <div style={{ maxWidth: 1180, margin: "0 auto" }}>
                        {stateBanner && (
                            <div data-testid="mesh-freshness" style={{ display: "flex", alignItems: "center", gap: 8, padding: "11px 14px", borderRadius: 11, marginBottom: 16, background: stateBanner.background, color: stateBanner.color, border: `1px solid color-mix(in srgb, ${stateBanner.color} 25%, transparent)` }}>
                                <span style={{ fontSize: 12.5, fontWeight: 750 }}>{stateBanner.title}</span>
                                <span style={{ flex: 1, fontSize: 12 }}>{stateBanner.detail}</span>
                                {stateBanner.retryable && (
                                    <button type="button" onClick={retry} style={{ minHeight: 28, padding: "0 11px", borderRadius: 8, border: `1px solid ${stateBanner.color}`, background: "transparent", color: stateBanner.color, fontFamily: "inherit", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>Retry</button>
                                )}
                            </div>
                        )}
                        <div style={{ display: "flex", gap: 28, alignItems: "stretch", flexWrap: "wrap" }}>
                            <div style={{ flex: "0 1 520px", minWidth: 320, display: "flex", flexDirection: "column" }}>
                                <div>
                                    <div style={{ fontSize: 14, fontWeight: 650, color: "var(--fg1)" }}>Build Your Context</div>
                                    <div style={{ fontSize: 12.5, color: "var(--fg3)", marginTop: 3, lineHeight: 1.5 }}>
                                        The greater the number, the better the context. Better context improves relevance and quality of output
                                    </div>
                                </div>
                                <div style={{ flex: 1, display: "flex", alignItems: "center", marginTop: -6 }}>
                                    <div style={{ width: "100%", maxWidth: 500, aspectRatio: "1 / 1", margin: "0 auto" }}>
                                        <StrengthWheel
                                            score={analysis.score}
                                            tier={analysis.tier}
                                            categoryScores={analysis.categoryScores}
                                            active={activeCategory}
                                            uid="review"
                                            discoverSubtitle={standalone ? "What is the context?" : undefined}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div style={{ flex: "1 1 440px", minWidth: 320, display: "flex", flexDirection: "column" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 11, flexWrap: "wrap" }}>
                                    <span style={{ fontSize: 14, fontWeight: 650, color: "var(--fg1)" }}>Where the score comes from</span>
                                    <span style={{ flex: 1 }} />
                                    <span style={{ fontSize: 11.5, color: "var(--fg4)" }}>
                                        {coverage ? `${coverage.evaluatedDrivers} of ${coverage.applicableDrivers} context checks evaluated` : "Context checks unavailable"}
                                    </span>
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                    {CATEGORIES.map(category => (
                                        <CategoryLane
                                            key={category.key}
                                            category={standalone && category.key === "discover"
                                                ? {
                                                      ...category,
                                                      subtitle: "What is the context?",
                                                      actionLabel: "Refresh Discover Context"
                                                  }
                                                : category}
                                            score={analysis.categoryScores?.[category.key] ?? null}
                                            recommendation={analysis.categoryRecommendations
                                                ? analysis.categoryRecommendations[category.key].recommendation
                                                : biggestWins[category.key] ?? null}
                                            recommendationStatus={analysis.categoryRecommendations?.[category.key]?.status}
                                            onRetryRecommendation={retryRecommendations}
                                            categoryStatus={analysis.categoryAnalysis?.status ?? null}
                                            sourceDiagnostic={category.key === "discover"
                                                ? null
                                                : analysis.categoryAnalysis?.sourceDiagnostics?.[category.key] ?? null}
                                            active={activeCategory === category.key}
                                            onAction={standalone
                                                ? category.key === "discover"
                                                    ? onOpenWorkspaceRefresh ?? null
                                                    : category.key === "file"
                                                    ? onOpenWorkspaceFiles ?? null
                                                    : category.key === "external" || category.key === "code"
                                                      ? onOpenWorkspaceIntegrations ?? null
                                                      : null
                                                : () => category.destination === "refresh"
                                                    ? onOpenRefresh?.(projectId!)
                                                    : onNavigate(category.destination)}
                                            onHover={setActiveCategory}
                                        />
                                    ))}
                                </div>
                                <div style={{ marginTop: 12, display: "flex", gap: 12, alignItems: "stretch", flexWrap: "wrap" }}>
                                    <div style={{ ...cardStyle, flex: "1 1 170px" }}>
                                        <div style={{ ...eyebrowStyle, marginBottom: 7 }}>Overall</div>
                                        <div style={{ display: "flex", alignItems: "baseline", gap: 9 }}>
                                            <span style={{ fontSize: 30, fontWeight: 700, fontFamily: "var(--font-mono)", color: "var(--fg1)", lineHeight: 1 }}>{analysis.score}</span>
                                            <span style={{ fontSize: 13, fontWeight: 700, color: overallBand.tone }}>
                                                {overallBand.label}
                                            </span>
                                        </div>
                                        <div style={{ fontSize: 12, color: "var(--fg3)", marginTop: 7, lineHeight: 1.5 }}>
                                            {weakestCategory
                                                ? `Held back mostly by ${weakestCategory.shortTitle}. ${overallBand.plain}`
                                                : overallBand.plain}
                                        </div>
                                    </div>
                                    <div
                                        ref={nextActionRef}
                                        tabIndex={-1}
                                        role="region"
                                        aria-labelledby="mesh-next-action-title"
                                        data-testid="next-priority-action"
                                        style={{ flex: "1.5 1 260px", padding: "13px 15px", borderRadius: 13, background: "color-mix(in srgb, #0f766e 6%, var(--bg-paper))", border: "1px solid rgba(15,118,110,0.30)" }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7, flexWrap: "wrap" }}>
                                            <div id="mesh-next-action-title" style={{ ...eyebrowStyle, color: "#0f766e" }}>Do this next</div>
                                            {nextActionCategory && (
                                                <span
                                                    data-testid="next-action-category"
                                                    style={{ padding: "2px 7px", borderRadius: 999, background: `color-mix(in srgb, ${nextActionCategory.color} 12%, transparent)`, color: nextActionCategory.accentColor, fontSize: 10.5, fontWeight: 700 }}
                                                >
                                                    {nextActionCategory.title}
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ fontSize: 13, color: "var(--fg1)", lineHeight: 1.55 }}>
                                            {nextBestAction ? (
                                                <>
                                                    {nextBestAction.message}
                                                    {(nextBestAction.estimatedScoreDelta ?? 0) > 0 && (
                                                        <span style={{ fontWeight: 700, color: "#0f766e" }}> Estimated +{nextBestAction.estimatedScoreDelta} points.</span>
                                                    )}
                                                </>
                                            ) : recommendationsPending
                                              ? "Analyzing recommendations…"
                                              : recommendationsFailed && retryRecommendations
                                                ? <button type="button" onClick={retryRecommendations} style={{ padding: 0, border: 0, background: "none", color: "inherit", font: "inherit", fontWeight: 700, cursor: "pointer" }}>Recommendation unavailable — Retry.</button>
                                                : "No prioritized action is available for the current analysis."}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
